import React, { useEffect, useState, useRef, useMemo } from "react";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faDoorOpen, faPhone, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import moment from "moment";

import { deSelectTeeTime, updateTemporaryBooking } from "redux/actions/teesheet";
import { closeBooking, showError, showSuccess, enqueue, dequeue } from "redux/actions/ui";
import { ICustomer } from "redux/reducers/models/customer";
import { ISlot, ITeeTime, ITeeTimeSlotNew, IBooking as IBookingModel } from "redux/reducers/models/teetime";

import { StatusCode } from "api/protocols";

import { convertTime, customerErrorMessage, delay, convertHoles } from "helpers/Helpers";
import { isTurnBlockedForEighteenHoles } from "helpers/teeSheetTurn";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Sheet from "components/sheet/Sheet";
import Tabs from "components/tabs/Tabs";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import TextField from "components/form/textField/TextField";
import Radio from "components/radio";
import { Dropdown } from "components/dropdown/dropdown";
import Popup from "components/popup/Popup";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import CreditCardCatalogue from "elements/teesheet/creditCard/CreditCardCatalogue";
import CreditCardAdd from "elements/teesheet/creditCard/CreditCardAdd";
import { useTranslation } from "react-i18next";

import "public/scss/teesheet.scss";
import "elements/teesheet/bookingModal.scss";
import Portal from "elements/Portal";
import Spin from "components/spin/spin";
import {
  GetCustomer,
  GetCustomerPaymentMethod,
  PostCustomer,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { GetCustomerFriends, ICustomerFriend } from "api/rpc/2024-04/facilityAdmin/customer/friend";
import { TCustomerType } from "redux/reducers/models/facility";
import {
  IPostBooking,
  PostBooking,
  PostBookingNote,
  PutBookingConfirmation,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import {
  DeleteTeeTimeLocks,
  GetTeeTime,
  IGetTeeTimeRes,
  PostTeeTimeLock,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { TLoadingAddBookingsAction, TLoadingTeetimeTabsActionStatus } from "../loadingTeetimeTabActions";
import { Badge } from "components/badge/Badge";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import PlayerSlots from "components/bookingPopUp/playersSlots/PlayerSlots";
import { ButtonNew } from "components/buttonNew";
import SelectNew from "components/select/SelectNew";

export interface ICartsAvailableNew {
  nineHole: number;
  eighteenHole: number;
}

interface IBookingModalNewProps {
  cartsAvailable: ICartsAvailableNew;
  slot: ITeeTimeSlotNew;
  getNextTeeTimeStartTime(currentTeeTime: ITeeTime): string;
  loading: boolean;
  loadingText: string;
  reloadTeeTimes: () => void;
  reloadActionTeeTimeTabs: (addTeetimeTabAction: TLoadingAddBookingsAction) => Promise<TLoadingTeetimeTabsActionStatus>;
  bookingGroupId?: string;
}

interface IMainState {
  [key: string]: any;
  golferSearchQuery1: string;
  golferSearchQuery2: string;
  golferSearchQuery3: string;
  golferSearchQuery4: string;
  golferSearchQuery5: string;
  golferSearchQuery6: string;
  golferSearchResult1: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult2: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult3: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult4: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult5: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult6: (Partial<ICustomer> | ICustomer)[];
  playingPartners: Partial<ICustomer>[];
  searching: boolean;
}

export interface INewBookingState {
  holes: number;
  quantity: number;
  teeTimeSlots: {
    id: number;
    customer_id: number;
    guest: boolean;
    first_name?: string;
    last_name?: string;
  }[];
  powerCartQuantity: number | string;
  customerSelection: ICustomer[];
  customer: any;
  customers: [];
  golferInitialLoad: any;
  cartInitialLoad: any;
  holesInitialLoad: any;
  saveCard: boolean;
  addCard: boolean;
  notes: any;
  noteDescription: any;
  notePlayer: number;
  addNoteVisible: boolean;
  notesTitleVisible: string;
  updateBookingActive: boolean;
  editBooking: undefined | Record<string, any>;
  paymentMethods: any[];
  selectedPaymentMethod?: number;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  cardReady: boolean;
  extraPlayersDropdownVisible: boolean;
  extraPlayersSlots: ISlot[][];
  /** Turn tee time ids for each overflow front tee time in extraPlayersSlots (18-hole bookings). */
  extraPlayersTurnTeeTimeIds: number[];
  teetimeLockPopupVisible: boolean;
  lockedTeeTime: string;
  extraPlayersQuantity: number;

  holeButtons: boolean[]; // disabled
  playerButtons: boolean[]; // disabled
  cartButtons: boolean[]; // disabled

  bookingWithinHour: boolean;

  bookingPosting: boolean;
  extraCartsDropdownVisible: boolean;
  sourceDropdownVisible: boolean;
  source: "tee_sheet" | "phone" | "walk_in";
}

export interface INewPlayerState {
  newCustomer: ICustomer;
  selectedSlot: ISlot;
  newCustomerSheetActive: boolean;
  currentSearch: string;
}

export interface IBooking {
  id: number;
  tee_time_id: number;
}

export enum StateType {
  MainState = "IMainState",
  NewBookingState = "INewBookingState",
  NewPlayerState = "INewPlayerState",
}

export interface IBookingNoteState {
  content: string;
  playerIndex: number;
}

/**
 * Credit card bypass only applies when every player type in the booking has
 * bypass_credit_card_required. Guest slots use the booker's guest type (or the
 * default green fee type), matching green fee pricing.
 */
function bookingPlayersBypassCreditCard(
  teeTimeSlots: Array<{ customer_id: number; guest: boolean }>,
  customerSelection: ICustomer[],
  customerTypes: TCustomerType[],
  quantity: number,
  primaryCustomerId?: number,
): boolean {
  if (!primaryCustomerId || quantity < 1 || customerTypes.length === 0) {
    return false;
  }

  const typeById = new Map(customerTypes.map(type => [type.id, type]));
  const defaultType =
    customerTypes.find(type => Boolean(type.default) && type.application === "green_fee") ??
    customerTypes.find(type => Boolean(type.default));

  const slots: Array<{ customer_id: number; guest: boolean }> = teeTimeSlots.map(slot => ({
    customer_id: slot.customer_id,
    guest: slot.guest,
  }));
  while (slots.length < quantity) {
    slots.push({ customer_id: primaryCustomerId, guest: true });
  }

  const typeIds: number[] = [];
  for (const slot of slots.slice(0, quantity)) {
    const customer = customerSelection.find(selection => selection.id === slot.customer_id);
    let typeId = customer?.customer_type_id ?? defaultType?.id ?? null;

    if (slot.guest) {
      const bookerType = typeId != null ? typeById.get(typeId) : undefined;
      if (
        bookerType?.guest_customer_type_id != null &&
        typeId != null &&
        defaultType?.id != null &&
        typeId !== defaultType.id
      ) {
        typeId = bookerType.guest_customer_type_id;
      } else {
        typeId = defaultType?.id ?? null;
      }
    }

    if (typeId == null) {
      return false;
    }
    typeIds.push(typeId);
  }

  return typeIds.every(typeId => Boolean(typeById.get(typeId)?.configuration?.bypass_credit_card_required));
}

function buildReloadTeeTimeIdsAfterBooking(params: {
  primaryTeeTimeId: number;
  extraPlayersSlots: ISlot[][];
  holes: number | null;
  primaryTurnTeeTimeId?: number;
  extraTurnTeeTimeIds: number[];
}): number[] {
  let frontTeeTimeIds: number[];

  if (params.extraPlayersSlots.length === 0) {
    frontTeeTimeIds = [params.primaryTeeTimeId];
  } else {
    frontTeeTimeIds = params.extraPlayersSlots.reduce(
      (accumulator, next) => {
        const nextTeeTimeId = next?.[0]?.tee_time_id;

        if (nextTeeTimeId != null) {
          accumulator.push(nextTeeTimeId);
        }

        return accumulator;
      },
      [params.primaryTeeTimeId],
    );
  }

  if (params.holes !== 18) {
    return frontTeeTimeIds;
  }

  const idsToLoad = [...frontTeeTimeIds];
  const turnTeeTimeIds = [params.primaryTurnTeeTimeId, ...params.extraTurnTeeTimeIds].filter(
    (id): id is number => id != null,
  );

  for (const turnTeeTimeId of turnTeeTimeIds) {
    if (!idsToLoad.includes(turnTeeTimeId)) {
      idsToLoad.push(turnTeeTimeId);
    }
  }

  return idsToLoad;
}

export default function BookingModalNew(props: IBookingModalNewProps) {
  const { Option } = Select;

  const { facilityStore, authStore, teeSheetStore, uiStore } = useAppSelector(state => state);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = authStore.user?.permissions;

  const [state, setState] = useState<IMainState>({
    golferSearchQuery1: "",
    golferSearchQuery2: "",
    golferSearchQuery3: "",
    golferSearchQuery4: "",
    golferSearchQuery5: "",
    golferSearchQuery6: "",
    golferSearchResult1: [null],
    golferSearchResult2: [null],
    golferSearchResult3: [null],
    golferSearchResult4: [null],
    golferSearchResult5: [null],
    golferSearchResult6: [null],
    playingPartners: [],
    searching: false,
  });

  const [newBookingState, setNewBookingState] = useState<INewBookingState>({
    holes: /*18*/ null,
    quantity: /*2*/ null,
    powerCartQuantity: /*0*/ null,
    teeTimeSlots: [],

    customerSelection: [],
    customer: null,
    customers: [],
    golferInitialLoad: null,
    cartInitialLoad: null,
    holesInitialLoad: null,
    notes: [],
    noteDescription: "",
    addNoteVisible: false,
    notesTitleVisible: "none",
    notePlayer: null,
    updateBookingActive: false,
    editBooking: undefined,
    saveCard: false,
    addCard: false,
    paymentMethods: undefined,
    selectedPaymentMethod: undefined,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    cardReady: false,
    extraPlayersDropdownVisible: false,
    extraPlayersSlots: [],
    extraPlayersTurnTeeTimeIds: [],
    teetimeLockPopupVisible: false,
    lockedTeeTime: "",
    extraPlayersQuantity: 0,

    holeButtons: [false, false], // disabled
    playerButtons: [false, false, false, false], // disabled
    cartButtons: [false, false], // disabled

    bookingWithinHour: false,

    bookingPosting: false,
    extraCartsDropdownVisible: false,
    sourceDropdownVisible: false,
    source: "tee_sheet",
  });

  const [newPlayerState, setNewPlayerState] = useState<INewPlayerState>({
    newCustomer: null,
    selectedSlot: null,
    newCustomerSheetActive: false,
    currentSearch: "",
  });

  const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>([]);

  const [newBookingCount, setNewBookingCount] = useState<number>(0);

  // FUNCTIONS

  function getBookingCartsAvailable() {
    if (!facilityStore.teeSheetSettings?.validate_power_cart_count) {
      return 0;
    } else if (newBookingState.holes === 9) {
      return props.cartsAvailable.nineHole;
    } else if (newBookingState.holes === 18) {
      return props.cartsAvailable.eighteenHole;
    } else {
      return 0;
    }
  }

  const bookingCartsAvailable = getBookingCartsAvailable();

  const [maxBookingSize, setMaxBookingSize] = useState(16);

  const startingNumber = 5; //UI always shows first 4 as buttons
  const extraPlayers = [...Array(maxBookingSize - startingNumber + 1).keys()].map(index => index + startingNumber);
  const extraPlayersRef = useRef(null);
  useOutsideAlerter(extraPlayersRef, () =>
    updateState<INewBookingState>({ extraPlayersDropdownVisible: false }, StateType.NewBookingState),
  );

  const extraCartsRef = useRef(null);
  useOutsideAlerter(extraCartsRef, () =>
    updateState<INewBookingState>({ extraCartsDropdownVisible: false }, StateType.NewBookingState),
  );

  const sourceRef = useRef(null);
  useOutsideAlerter(sourceRef, () =>
    updateState<INewBookingState>({ sourceDropdownVisible: false }, StateType.NewBookingState),
  );

  function updateState<T>(newState: Partial<T>, type: StateType) {
    const states = {
      [StateType.MainState]: (newState: Partial<T>) =>
        setState((cur: IMainState) => {
          return { ...cur, ...newState };
        }),
      [StateType.NewBookingState]: (newState: Partial<T>) =>
        setNewBookingState((cur: INewBookingState) => {
          return { ...cur, ...newState };
        }),
      [StateType.NewPlayerState]: (newState: Partial<T>) =>
        setNewPlayerState((cur: INewPlayerState) => {
          return { ...cur, ...newState };
        }),
    };

    states[type](newState);
  }

  const [sendBookingConfirmation, setSendBookingConfirmation] = useState<boolean>(true);

  const postBooking = async (input: IPostBooking) => {
    const bookingSource = facilityStore.teeSheetSettings?.display_booking_source
      ? newBookingState.source === "walk_in"
        ? "walk_in"
        : "phone"
      : "tee_sheet";

    const bookingRes = await PostBooking(
      {
        ...input,
        power_cart_quantity: input.power_cart_quantity > 2 ? 2 : input.power_cart_quantity,
        booking_group_id: props?.bookingGroupId ?? undefined,
        source: bookingSource,
      },
      false,
    );

    if (bookingRes.status !== StatusCode.OK) {
      if (bookingRes.status === StatusCode.BAD_REQUEST) {
        dispatch(showError(bookingRes.data as string));
      } else {
        dispatch(showError(t("Error creating booking")));
      }

      return;
    }

    // Increment count for the first booking
    setNewBookingCount(1);

    let remainingPowerCarts = input.power_cart_quantity - bookingRes?.data?.power_cart_quantity;
    // Handle booking extra players (quantity > 4)
    let extraBookingCount = 1;
    for (const teeTime of newBookingState.extraPlayersSlots) {
      const slotsFormatted: { id: number; customer_id: number; guest: boolean }[] = [];

      teeTime.forEach(slot => {
        slotsFormatted.push({
          id: slot.id,
          customer_id: newBookingState.customerSelection[0].id,
          guest: true,
        });
      });

      const extraBookingRes = await PostBooking(
        {
          ...input,
          tee_time_id: teeTime[0]?.tee_time_id,
          quantity: teeTime.length,
          tee_time_slots: slotsFormatted,
          booking_group_id: bookingRes.data.booking_group_id,
          power_cart_quantity: remainingPowerCarts >= 2 ? 2 : remainingPowerCarts,
          source: bookingSource,
        },
        false,
      );

      remainingPowerCarts -= 2;

      if (remainingPowerCarts < 0) {
        remainingPowerCarts = 0;
      }

      if (extraBookingRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error creating booking")));
        return;
      }

      extraBookingCount++;
      setNewBookingCount(extraBookingCount);
    }

    // Send group booking email if extra players exist
    if (sendBookingConfirmation) {
      const confirmationRes = await PutBookingConfirmation(
        { booking_group_id: bookingRes.data.booking_group_id },
        false,
      );

      if (confirmationRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error sending confirmation email")));
        return;
      }
    }

    if (bookingRes.status === StatusCode.OK) {
      dispatch(showSuccess(t("Booking successfully saved!")));
      return bookingRes.data as IBookingModel;
    }

    return;
  };

  const bookCustomer = async () => {
    const tee_time_id = teeSheetStore.selectedTeeTime.id;
    const teeTimeSlots = newBookingState.teeTimeSlots;

    const power_cart_quantity =
      newBookingState.powerCartQuantity === "none" ? 0 : (newBookingState.powerCartQuantity as number);

    const bookingQuantity = newBookingState.quantity - newBookingState.extraPlayersQuantity;

    // Handle booking guests
    if (newBookingState.quantity > newBookingState.teeTimeSlots.length) {
      const diff = bookingQuantity - newBookingState.teeTimeSlots.length;
      const remainingSlots = teeSheetStore.selectedTeeTime.slots.filter(
        slot =>
          slot.status === "available" &&
          !newBookingState.teeTimeSlots.some(selectedSlot => slot.id === selectedSlot.id),
      );
      for (let i = 0; i < diff; i++) {
        teeTimeSlots.push({
          id: remainingSlots[i].id,
          customer_id: newBookingState.customerSelection[0].id,
          guest: true,
        });
      }
      updateState<INewBookingState>({ teeTimeSlots }, StateType.NewBookingState);
    }

    const booking: IPostBooking = {
      tee_time_id,
      send_confirmation: false,
      quantity: bookingQuantity,
      holes: newBookingState.holes,
      power_cart_quantity: power_cart_quantity,
      customer_id: newBookingState.customerSelection[0].id,
      tee_time_slots: newBookingState.teeTimeSlots,
      customer_payment_method_id: newBookingState.selectedPaymentMethod,
    };

    // Handle collecting payment method
    if (elementsComplete) {
      const collectCardRes = await collectCard(newBookingState.customerSelection[0].id);
      if (!collectCardRes) {
        updateState<INewBookingState>({ bookingPosting: false }, StateType.NewBookingState);
        return;
      }
      booking.customer_payment_method_id = collectCardRes.customer_payment_method_id;
    }

    updateState<INewBookingState>({ bookingPosting: true }, StateType.NewBookingState);

    dispatch(updateTemporaryBooking(booking));

    const bookingId = await postBooking(booking);

    for (let i = 0; i < newBookingState.notes.length; i++) {
      const noteInfo = {
        tee_time_booking_id: bookingId.id,
        content: String(newBookingState.notes[i].description as string),
        tee_time_booking_participant_id: Number(
          bookingId?.participants?.[newBookingState.notes[i].participantIndex]?.id,
        ),
      };

      const bookingNoteRes = await PostBookingNote(noteInfo, false);

      if (bookingNoteRes.status !== StatusCode.OK) {
        dispatch(showError(bookingNoteRes.data.message as string));
        return;
      }
    }

    handleTabChange(0);

    const teetimeIdsToLoad = buildReloadTeeTimeIdsAfterBooking({
      primaryTeeTimeId: tee_time_id,
      extraPlayersSlots: newBookingState.extraPlayersSlots ?? [],
      holes: newBookingState.holes,
      primaryTurnTeeTimeId: teeSheetStore.selectedTeeTime?.turn_tee_time?.id,
      extraTurnTeeTimeIds: newBookingState.extraPlayersTurnTeeTimeIds ?? [],
    });

    // force refresh, player appears
    void props.reloadActionTeeTimeTabs({
      type: "add-bookings",
      ids: teetimeIdsToLoad,
    });

    closeNewBooking();
  };

  function closeNewBooking() {
    void deleteTeeTimeLocks();
    dispatch(closeBooking());
    updateState<INewBookingState>(
      {
        updateBookingActive: false,
        customerSelection: [],
        selectedPaymentMethod: undefined,
        elementComplete: {
          cardCvc: false,
          cardExpiry: false,
          cardNumber: false,
        },
        addNoteVisible: false,
        notesTitleVisible: "none",
        noteDescription: "",
        notes: [],
        quantity: null,
        holes: null,
        powerCartQuantity: null,
        paymentMethods: undefined,
        teeTimeSlots: [],
        addCard: false,
        saveCard: false,
        holeButtons: [false, false], // disabled
        playerButtons: [false, false, false, false], // disabled
        cartButtons: [false, false], // disabled
        extraPlayersDropdownVisible: false,
        extraPlayersSlots: [],
        extraPlayersTurnTeeTimeIds: [],
        teetimeLockPopupVisible: false,
        lockedTeeTime: "",
        extraPlayersQuantity: 0,
        bookingPosting: false,
        sourceDropdownVisible: false,
        source: facilityStore.teeSheetSettings?.display_booking_source ? "phone" : "tee_sheet",
      },
      StateType.NewBookingState,
    );

    // clear search and result
    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
        golferSearchResult1: [null],
        golferSearchResult2: [null],
        golferSearchResult3: [null],
        golferSearchResult4: [null],
        golferSearchResult5: [null],
        golferSearchResult6: [null],
        playingPartners: [],
        searching: false,
      },
      StateType.MainState,
    );

    dispatch(deSelectTeeTime());
    setSelected(0);
    setNewBookingCount(0);
  }

  async function deleteTeeTimeLocks() {
    const deleteLocksRes = await DeleteTeeTimeLocks(false);
    if (deleteLocksRes.status !== StatusCode.OK) {
      return;
    }
  }

  const handleCustomerSearch = (query: string, name: string) => {
    // void loadCustomers(query);

    updateState<IMainState>({ [name]: query }, StateType.MainState);
    setNewPlayerState(prevState => ({
      ...prevState,
      currentSearch: query,
    }));
  };

  const loadPlayingPartners = async (customer_id: number) => {
    const partnersRes = await GetCustomerFriends({ customer_id }, false);
    if (partnersRes.status !== StatusCode.OK) {
      updateState<IMainState>({ playingPartners: [] }, StateType.MainState);
      return;
    }

    const normalized: Partial<ICustomer>[] = (partnersRes.data ?? []).map((friend: ICustomerFriend) => ({
      id: friend.id,
      full_name: friend.full_name,
      first_name: friend.first_name,
      last_name: friend.last_name,
      email: friend.email,
      phone: friend.phone,
      member_code: friend.member_code,
      member_number: friend.member_number ?? null,
      customer_type: Array.isArray(friend.customer_type) ? friend.customer_type.map(type => type.title).join(", ") : "",
    }));

    updateState<IMainState>({ playingPartners: normalized }, StateType.MainState);
  };

  useEffect(() => {
    const primaryCustomerId = newBookingState.customerSelection?.[0]?.id;
    if (!primaryCustomerId) {
      updateState<IMainState>({ playingPartners: [] }, StateType.MainState);
      return;
    }

    void loadPlayingPartners(primaryCustomerId);
  }, [newBookingState.customerSelection?.[0]?.id]);

  async function loadCustomers(golferSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: golferSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  const search = async (
    mounted: boolean,
    golferSearchQuery: string,
    golferSearchResult: string,
    cancelToken: CancelToken,
  ) => {
    try {
      if (golferSearchQuery === "") {
        if (mounted) {
          updateState<IMainState>({ [golferSearchResult]: [null] }, StateType.MainState);
        }
        return;
      } else {
        updateState<IMainState>({ searching: true }, StateType.MainState);
        const res = await loadCustomers(golferSearchQuery, cancelToken);
        if (mounted) {
          updateState<IMainState>({ [golferSearchResult]: res, searching: false }, StateType.MainState);
        }
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  };

  // obtain dynamic value for extraPlayers array value.
  useEffect(() => {
    if (facilityStore.teeSheetSettings !== null) {
      setMaxBookingSize(facilityStore.teeSheetSettings.max_booking_size);
    }
  }, [facilityStore.teeSheetSettings]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery1, "golferSearchResult1", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult1: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery1]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery2, "golferSearchResult2", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult2: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery2]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery3, "golferSearchResult3", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult3: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery3]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery4, "golferSearchResult4", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult4: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery4]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery5, "golferSearchResult5", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult5: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery5]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery6, "golferSearchResult6", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult6: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery6]);

  /**
   * compares tee_time_quantity and turn_tee_time quantity and returns the lower quantity
   * if turn_tee_time_quantity is null turn_tee_time will be returned
   * @param tee_time_quantity
   * @param turn_tee_time_quantity
   * @returns number
   */
  const lowerQuantity = (tee_time_quantity: number, turn_tee_time_quantity: number, selected_holes: number): number => {
    if (turn_tee_time_quantity === null) {
      return tee_time_quantity;
    } else if (selected_holes === 9) {
      return tee_time_quantity;
    } else {
      return Math.min(tee_time_quantity, turn_tee_time_quantity);
    }
  };

  useEffect(() => {
    if (!teeSheetStore.selectedTeeTime) {
      return;
    }

    // disabling options...
    let mounted = true;

    const disableOptions = () => {
      const holeButtons: boolean[] = [true, true];
      let playerButtons: boolean[] = teeSheetStore.selectedTeeTime?.slots?.map(() => false);
      const quantity_remaining = lowerQuantity(
        teeSheetStore.selectedTeeTime?.quantity_remaining,
        teeSheetStore.selectedTeeTime?.turn_tee_time?.quantity_remaining,
        newBookingState.holes,
      );

      // disabling number of players based on quantity remaining
      switch (quantity_remaining) {
        case 1:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i === 0) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 2:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i === 0 || i === 1) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 3:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i !== 3) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 4:
          playerButtons = playerButtons.map(disabled => {
            disabled = false;
            return disabled;
          });
          break;

        case 5:
          playerButtons = playerButtons.map(disabled => {
            disabled = false;
            return disabled;
          });
          break;

        case 6:
          playerButtons = playerButtons.map(disabled => {
            disabled = false;
            return disabled;
          });
          break;
      }

      let bookingQuantity = newBookingState.quantity;
      if (bookingQuantity > teeSheetStore.selectedTeeTime?.slots?.length) {
        bookingQuantity = bookingQuantity - newBookingState.extraPlayersQuantity;
      }

      // Disabling holes
      teeSheetStore.selectedTeeTime.divisions_available?.forEach(division => {
        // 9 holes available
        if (division === 1) {
          holeButtons[1] = false;
        } else if (division === 2) {
          if (
            (teeSheetStore.selectedTeeTime.divisions_available?.length > 1 &&
              teeSheetStore.selectedTeeTime?.turn_tee_time === null) ||
            isTurnBlockedForEighteenHoles(
              teeSheetStore?.selectedTeeTime?.turn_tee_time?.blocked_type,
              teeSheetStore?.selectedTeeTime?.blocked_type,
            ) ||
            ((teeSheetStore.selectedTeeTime?.turn_tee_time?.league_id ||
              teeSheetStore.selectedTeeTime?.turn_tee_time?.tournament_id) &&
              (teeSheetStore.selectedTeeTime?.league_id !== teeSheetStore.selectedTeeTime?.turn_tee_time?.league_id ||
                teeSheetStore.selectedTeeTime?.tournament_id !==
                teeSheetStore.selectedTeeTime?.turn_tee_time?.tournament_id))
          ) {
            holeButtons[1] = false;
          } else if (teeSheetStore.selectedTeeTime?.turn_tee_time != null) {
            // 18 holes available
            if (teeSheetStore.selectedTeeTime?.turn_tee_time?.quantity_remaining >= bookingQuantity) {
              holeButtons[0] = false;
            } else {
              // Only 9 holes available
              holeButtons[1] = false;
            }
          }
        }
      });

      if (mounted) {
        if (holeButtons[0] && holeButtons[1]) {
          updateState<INewBookingState>({ holeButtons, playerButtons, holes: null }, StateType.NewBookingState);
        } else if (holeButtons[0]) {
          updateState<INewBookingState>({ holeButtons, playerButtons, holes: 9 }, StateType.NewBookingState);
        } else if (holeButtons[1]) {
          updateState<INewBookingState>({ holeButtons, playerButtons, holes: 18 }, StateType.NewBookingState);
        }
        updateState<INewBookingState>({ holeButtons, playerButtons }, StateType.NewBookingState);
      }
    };

    const run = async () => {
      if (uiStore.newBookingActive === true) {
        // TODO: Figure out what's going on here
        // dispatch(enqueue());

        await delay(100);

        disableOptions();

        await delay(1000);

        // dispatch(dequeue());
      }
    };
    void run();

    return () => {
      mounted = false;
    };
  }, [teeSheetStore.selectedTeeTime, newBookingState.holes, uiStore.newBookingActive, newBookingState.quantity]);

  useEffect(() => {
    let initialQuantity = 0;
    let maxQuantity = 0;
    let currentBookings = 0;

    if (props.slot !== null) {
      if (teeSheetStore?.selectedTeeTime?.quantity_remaining === teeSheetStore?.selectedTeeTime?.slots?.length) {
        initialQuantity = props.slot?.tee_time_slot_position;
      } else {
        for (let i = 0; i < teeSheetStore?.selectedTeeTime?.slots?.length; i++) {
          if (teeSheetStore?.selectedTeeTime?.slots[i]?.status === "booked") {
            currentBookings = currentBookings + 1;
          }
        }
        initialQuantity = props.slot?.tee_time_slot_position - currentBookings;
      }
    } else {
      maxQuantity = teeSheetStore?.selectedTeeTime?.quantity_remaining;
    }

    const defaultHoles = facilityStore.teeSheetSettings?.default_booking_holes ?? 18;

    if (teeSheetStore.selectedTeeTime?.cart_rule == "required") {
      updateState<INewBookingState>(
        {
          holes: defaultHoles,
          quantity: props.slot !== null ? initialQuantity : maxQuantity,
          powerCartQuantity: 1,
          source: facilityStore.teeSheetSettings?.display_booking_source ? "phone" : "tee_sheet",
        },
        StateType.NewBookingState,
      );
      return;
    }

    updateState<INewBookingState>(
      {
        holes: defaultHoles,
        quantity: props.slot !== null ? initialQuantity : maxQuantity,
        powerCartQuantity: "none",
        source: facilityStore.teeSheetSettings?.display_booking_source ? "phone" : "tee_sheet",
      },
      StateType.NewBookingState,
    );
  }, [uiStore.newBookingActive, teeSheetStore.selectedTeeTime]);

  useEffect(() => {
    const selectedTeeTimeDateTime = new Date(
      moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format("LLL"),
    );

    const hourBeforeSelectedTeeTimeDateTime = new Date(
      moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format("LLL"),
    );

    hourBeforeSelectedTeeTimeDateTime.setHours(hourBeforeSelectedTeeTimeDateTime.getHours() - 1);

    const currentTime = moment();

    const bookingWithinHour = currentTime.isBetween(hourBeforeSelectedTeeTimeDateTime, selectedTeeTimeDateTime);

    updateState<INewBookingState>({ bookingWithinHour: bookingWithinHour }, StateType.NewBookingState);
  }, [uiStore.newBookingActive]);

  useEffect(() => {
    if (!uiStore.newBookingActive || !teeSheetStore.selectedTeeTime?.credit_card_required) {
      return;
    }

    const source = axios.CancelToken.source();
    void (async () => {
      const typesRes = await GetCustomerType(
        { application: "green_fee", configuration: true },
        false,
        source.token,
      );
      if (typesRes.status === StatusCode.OK) {
        setCustomerTypes(typesRes.data ?? []);
      }
    })();

    return () => {
      source.cancel("Cancelled");
    };
  }, [uiStore.newBookingActive, teeSheetStore.selectedTeeTime?.credit_card_required]);

  useEffect(() => {
    if (newBookingState.teeTimeSlots[0]?.customer_id) {
      void loadCustomersPaymentMethods(newBookingState.teeTimeSlots[0]?.customer_id);
    } else if (newBookingState?.editBooking?.customer_id) {
      void loadCustomersPaymentMethods(newBookingState.editBooking.customer_id as number);
    }

    if (!newBookingState.teeTimeSlots[0]?.customer_id) {
      updateState<INewBookingState>({ paymentMethods: undefined }, StateType.NewBookingState);
    }
  }, [newBookingState.customerSelection]);

  const handleCustomerSelection = (id: number | string, slot: ISlot, newCustomer: Record<string, any>) => {
    if (newBookingState.customerSelection.length === teeSheetStore.selectedTeeTime?.slots?.length) {
      return;
    }

    if (id === "") {
      return;
    } else {
      const golferPosition = slot.position.toString();
      const queryKey = "golferSearchQuery".concat(golferPosition);
      const searchResultKey = "golferSearchResult".concat(golferPosition);

      const selection = !newCustomer
        ? ((): (Partial<ICustomer> | ICustomer)[] => {
          const candidates = state[queryKey]
            ? (state[searchResultKey] as (Partial<ICustomer> | ICustomer)[])
            : state.playingPartners;

          const selectedCustomer = (candidates ?? []).find(
            candidate => candidate != null && Number(candidate.id) === Number(id),
          );

          return selectedCustomer ? [selectedCustomer] : [];
        })()
        : [newCustomer];

      if (!selection[0]) {
        return;
      }

      const customerSelection = newBookingState.customerSelection;
      customerSelection.splice(slot.position - 1, 0, selection[0] as ICustomer);

      const teeTimeSlots = newBookingState.teeTimeSlots;
      teeTimeSlots.splice(slot.position - 1, 0, { id: slot.id, customer_id: selection[0].id, guest: false });

      updateState({ customerSelection, teeTimeSlots }, StateType.NewBookingState);
    }
  };

  const handleSetAsGuest = (slot: ISlot) => {
    if (slot.position === 1) {
      return;
    }

    const golferPosition = slot.position.toString();
    const queryKey = "golferSearchQuery".concat(golferPosition);
    const searchName = String(state[queryKey] ?? "").trim();

    if (!searchName) {
      return;
    }

    if (!newBookingState.customerSelection[0]?.id) {
      dispatch(showError(t("Please select a customer for the booking first")));
      return;
    }

    if (newBookingState.teeTimeSlots.length === teeSheetStore.selectedTeeTime?.slots?.length) {
      return;
    }

    const nameParts = searchName.split(/\s+/);
    const first_name = nameParts[0];
    const last_name = nameParts.slice(1).join(" ");
    const bookerId = newBookingState.customerSelection[0].id;

    const guestPlayer = {
      id: bookerId,
      full_name: searchName,
      first_name,
      last_name,
      email: null,
      phone: null,
      customer_type: null,
    } as ICustomer;

    const customerSelection = [...newBookingState.customerSelection];
    customerSelection.splice(slot.position - 1, 0, guestPlayer);

    const teeTimeSlots = [...newBookingState.teeTimeSlots];
    teeTimeSlots.splice(slot.position - 1, 0, {
      id: slot.id,
      customer_id: bookerId,
      guest: true,
      first_name,
      last_name,
    });

    updateState<INewBookingState>({ customerSelection, teeTimeSlots }, StateType.NewBookingState);
    updateState<IMainState>({ [queryKey]: "" }, StateType.MainState);
  };

  const closeNewCustomer = () => {
    updateState<INewPlayerState>(
      {
        newCustomer: null,
        selectedSlot: null,
        newCustomerSheetActive: false,
      },
      StateType.NewPlayerState,
    );

    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
      },
      StateType.MainState,
    );
  };

  const addCustomer = async (customerInfo: ICustomerInfoState) => {
    const customer = await createCustomer(customerInfo);
    if (customer) {
      handleCustomerSelection(customer.id, newPlayerState.selectedSlot, customer);
    }
  };

  const createCustomer = async (customerInfo: ICustomerInfoState) => {
    const error =
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "");

    if (error) {
      return null;
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        state: "disabled",
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (Number(customerRes.status) !== 200) {
      dispatch(showError(customerErrorMessage(t, customerRes?.data?.message as string)));
    }

    if (Number(customerRes.status) === 200) {
      updateState<INewPlayerState>({ newCustomer: customerRes.data.data }, StateType.NewPlayerState);
      void closeNewCustomer();
      // dispatch(showSuccess(customerRes.data.message));
      dispatch(showSuccess(t("Customer created successfull")));
      return customerRes.data as ICustomer;
    }
    return null;
  };

  const removeGolfer = (slotToRemove: { id: number; customer_id: number; guest: boolean }) => {
    const deleteIndex = newBookingState.teeTimeSlots.findIndex(slot => slot.id === slotToRemove.id);
    if (deleteIndex === -1) {
      return;
    }

    let customerSelection = [...newBookingState.customerSelection];
    let teeTimeSlots = [...newBookingState.teeTimeSlots];

    const playerAndGuests = teeTimeSlots.filter(slot => slot.customer_id === slotToRemove.customer_id);

    if (playerAndGuests.length > 1 && slotToRemove.guest === false) {
      customerSelection = customerSelection.filter(customer => customer.id !== slotToRemove.customer_id);
      teeTimeSlots = teeTimeSlots.filter(slot => slot.customer_id !== slotToRemove.customer_id);
    } else {
      customerSelection.splice(deleteIndex, 1);
      teeTimeSlots.splice(deleteIndex, 1);
    }

    updateState<INewBookingState>({ customerSelection, teeTimeSlots }, StateType.NewBookingState);
  };

  const onSelectCreditCard = (id: number) => {
    if (id > 0) {
      if (id === newBookingState.selectedPaymentMethod) {
        // unselect
        updateState<INewBookingState>({ selectedPaymentMethod: undefined }, StateType.NewBookingState);
      } else {
        updateState<INewBookingState>({ selectedPaymentMethod: id, addCard: false }, StateType.NewBookingState);
      }
    } else {
      dispatch(showError(t("Error selecting credit card.")));
    }
  };

  const handleNoteInput = (event: any) => {
    updateState<INewBookingState>({ noteDescription: event.target.value }, StateType.NewBookingState);
  };

  const loadCustomersPaymentMethods = async (customer_id: number) => {
    const paymentMethodRes = await GetCustomerPaymentMethod({ customer_id }, false);
    if (paymentMethodRes.status === StatusCode.OK) {
      updateState<INewBookingState>({ paymentMethods: paymentMethodRes.data }, StateType.NewBookingState);
    } else {
      dispatch(showError(paymentMethodRes.message));
      updateState<INewBookingState>({ paymentMethods: [] }, StateType.NewBookingState);
    }
  };

  //Function to save card to customer and to booking
  const setupCard = async (payment_method: string, customer_id: number) => {
    const setupRes = await PostSetup({ customer_id }, true);

    if (setupRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error setting up payment method")));
      return { success: false };
    } else {
      if (payment_method !== "undefined") {
        const stripeRes = await stripe.confirmCardSetup(setupRes.data.setup_intent.client_secret as string, {
          payment_method: payment_method,
        });

        if (stripeRes.error) {
          dispatch(showError(stripeRes.error.message));
        } else {
          const methodRes = await PostPaymentMethod({
            payment_method_id: stripeRes.setupIntent.payment_method,
            customer_id: customer_id,
            save_card: true,
            save_on_profile: newBookingState.saveCard,
          });
          if (methodRes.status !== StatusCode.OK) {
            dispatch(showError(methodRes.data.message as string));
            return { success: false };
          } else {
            dispatch(showSuccess(methodRes.data.message as string));
            updateState<INewBookingState>({ selectedPaymentMethod: methodRes.data.id }, StateType.NewBookingState);

            if (methodRes.status !== StatusCode.OK) {
              dispatch(showError(methodRes.data.message as string));
              return { success: false };
            }

            return {
              customer_payment_method_id: methodRes.data.id as number,
              success: true,
            };
          }
        }
      }
    }
  };

  //Function to save card
  const stripe = useStripe();
  const elements = useElements();

  const collectCard = async (customer_id: number) => {
    if (!stripe || !elements) {
      dispatch(showError(t("Error occurred before card could save. Please reattempt.")));
      return;
    }

    // Loader for length of operation to stop user from clicking
    // TODO: Handle this without global loader
    dispatch(enqueue());

    try {
      let setupCardRes;
      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (paymentMethod.paymentMethod) {
        setupCardRes = await setupCard(paymentMethod.paymentMethod.id, customer_id);
        dispatch(dequeue());
        return {
          customer_payment_method_id: setupCardRes.customer_payment_method_id,
        };
      } else {
        dispatch(showError(paymentMethod.error.message));
      }
    } catch (e) {
      console.error(e);
      dispatch(dequeue());
    }

    dispatch(dequeue());
  };

  // Handle tab change
  const [selected, setSelected] = useState(0);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);

    if (selectedTabIndex === 0) {
      updateState<INewBookingState>(
        {
          elementComplete: {
            cardNumber: false,
            cardExpiry: false,
            cardCvc: false,
          },
        },
        StateType.NewBookingState,
      );
    }
  };

  const handleHolesChange = (e: number) => {
    const holes = e;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      const numericPowerCartQuantity =
        newBookingState.powerCartQuantity === "none" ? 0 : (newBookingState.powerCartQuantity as number);

      let updatedPowerCartQuantity: string | number = null;

      if (holes === 9 && numericPowerCartQuantity > props.cartsAvailable.nineHole) {
        updatedPowerCartQuantity = props.cartsAvailable.nineHole === 0 ? "none" : props.cartsAvailable.nineHole;
      } else if (holes === 18 && numericPowerCartQuantity > props.cartsAvailable.eighteenHole) {
        updatedPowerCartQuantity = props.cartsAvailable.eighteenHole === 0 ? "none" : props.cartsAvailable.eighteenHole;
      } else {
        updatedPowerCartQuantity = newBookingState.powerCartQuantity;
      }

      updateState<INewBookingState>({ holes, powerCartQuantity: updatedPowerCartQuantity }, StateType.NewBookingState);
    } else {
      updateState<INewBookingState>({ holes }, StateType.NewBookingState);
    }
  };

  const handlePlayersChange = async (e: number) => {
    const quantity = e;

    if (quantity !== -1) {
      //Delete the tee time locks for the other tee times and create one for the current tee time
      if (newBookingState.extraPlayersQuantity !== 0) {
        const deleteTeeTimeLockRes = await DeleteTeeTimeLocks(true);
        if (deleteTeeTimeLockRes.status !== StatusCode.OK) {
          dispatch(showError(deleteTeeTimeLockRes.data.message as string));
        }
        const postTeeTimeLockRes = await PostTeeTimeLock({ tee_time_id: teeSheetStore.selectedTeeTime.id }, true);
        if (postTeeTimeLockRes.status !== StatusCode.OK) {
          dispatch(showError(postTeeTimeLockRes.data.message as string));
        }
      }
      // If the new selected quanitity is less than the previous quantity, remove the extra players
      if (quantity < newBookingState.quantity) {
        void handleRemoverGolfers(quantity);
      }
      updateState<INewBookingState>(
        {
          quantity,
          extraPlayersSlots: [],
          extraPlayersTurnTeeTimeIds: [],
          extraPlayersQuantity: 0,
        },
        StateType.NewBookingState,
      );
    }
  };

  const handleRemoverGolfers = (newQuantity: number) => {
    const updatedCustomerSelection = newBookingState.customerSelection.filter(
      (customer, index) => index + 1 <= newQuantity,
    );
    const updatedTeeTimeSlots = newBookingState.teeTimeSlots.filter((slot, index) => index + 1 <= newQuantity);
    updateState<INewBookingState>(
      { customerSelection: updatedCustomerSelection, teeTimeSlots: updatedTeeTimeSlots },
      StateType.NewBookingState,
    );
    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
      },
      StateType.MainState,
    );
  };

  const handleCartChange = (e: string | number) => {
    const powerCartQuantity = e;
    updateState<INewBookingState>({ powerCartQuantity }, StateType.NewBookingState);
  };

  const elementsComplete =
    newBookingState.elementComplete.cardNumber === true &&
    newBookingState.elementComplete.cardExpiry === true &&
    newBookingState.elementComplete.cardCvc === true;

  const playersBypassCreditCard = useMemo(
    () =>
      bookingPlayersBypassCreditCard(
        newBookingState.teeTimeSlots,
        newBookingState.customerSelection,
        customerTypes,
        newBookingState.quantity ?? 0,
        newBookingState.customerSelection[0]?.id,
      ),
    [
      newBookingState.teeTimeSlots,
      newBookingState.customerSelection,
      customerTypes,
      newBookingState.quantity,
    ],
  );

  const cc_required = Boolean(teeSheetStore.selectedTeeTime?.credit_card_required) && !playersBypassCreditCard;
  const cc_unselected = newBookingState.selectedPaymentMethod === undefined && !elementsComplete;
  const cc_override = permissions?.tee_sheet_credit_card_override;

  const handleCardSectionChange = (
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) => {
    if (newBookingState.selectedPaymentMethod !== undefined) {
      updateState<INewBookingState>({ selectedPaymentMethod: undefined }, StateType.NewBookingState);
    }

    setNewBookingState(prev => ({
      ...prev,
      elementComplete: { ...prev.elementComplete, [e.elementType]: e.complete},
    }));
  };

  const extraPlayersMenu = () => {
    return (
      <ul className="extra-players-menu">
        {extraPlayers.map(playerNum => {
          return (
            <li
              key={playerNum}
              className="extra-players-menu-item"
              onClick={() => void selectExtraPlayersQuantity(playerNum)}
            >
              <span>{playerNum}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  const extraPowerCartMenu = () => {
    const additionalCartNumbers = newBookingState.quantity / 2;
    const cartOptions = [];

    for (let i = 3; i <= Math.ceil(additionalCartNumbers); i++) {
      cartOptions.push(
        <li className="extra-carts-menu-item" key={i} onClick={() => selectExtraCartQuant(i)}>
          {i}
        </li>,
      );
    }

    return <ul className="extra-carts-menu">{cartOptions}</ul>;
  };

  const bookingSourceOptions: Array<{
    value: "phone" | "walk_in";
    label: string;
    icon: typeof faPhone;
  }> = [
    { value: "phone", label: t("Phone"), icon: faPhone },
    { value: "walk_in", label: t("Walk In"), icon: faDoorOpen },
  ];

  const sourceMenu = () => {
    return (
      <ul className="extra-players-menu booking-source-menu">
        {bookingSourceOptions.map(option => (
          <li
            key={option.value}
            className="extra-players-menu-item"
            onClick={() => selectBookingSource(option.value)}
          >
            <FontAwesomeIcon icon={option.icon} className="mr-2" />
            <span>{option.label}</span>
          </li>
        ))}
      </ul>
    );
  };

  const selectBookingSource = (source: "phone" | "walk_in") => {
    updateState<INewBookingState>(
      { source, sourceDropdownVisible: false },
      StateType.NewBookingState,
    );
  };

  const handleSourceDropdownVisible = () => {
    updateState<INewBookingState>({ sourceDropdownVisible: true }, StateType.NewBookingState);
  };

  // Organize away from rendering
  const tabs = [
    {
      id: "booking-details",
      content: t("General Info"),
    },
    {
      id: "credit-card",
      content: t("Payment Method"),
    },
    {
      id: "notes",
      content: (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {t("Notes")}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "20px",
              height: "20px",
              padding: "0 6px",
              backgroundColor: "#F3F4F6",
              color: "#6B7280",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: "500",
              lineHeight: "1",
            }}
          >
            {newBookingState.notes?.length || 0}
          </span>
        </span>
      ),
    },
  ];

  const selectExtraCartQuant = (quantity: number) => {
    setNewBookingState(prev => ({ ...prev, powerCartQuantity: quantity }));
    updateState<INewBookingState>({ extraCartsDropdownVisible: false }, StateType.NewBookingState);
  };

  const selectExtraPlayersQuantity = async (quantity: number) => {
    const nextTeeTimeSlots: ISlot[][] = [];
    const nextTurnTeeTimeIds: number[] = [];
    const tee_time_ids: number[] = [teeSheetStore.selectedTeeTime.id];
    let errorCheck = false;

    let calculateLimit = quantity / teeSheetStore.selectedTeeTime?.slots?.length;
    if (calculateLimit % 1 === 0) {
      calculateLimit -= 1;
    }

    const times = await getNextTeeTimes(Math.floor(calculateLimit));

    // Remove the selected tee times from the nextTeeTimes response
    const nextTeeTimes = times?.filter(teetime => teetime.id !== teeSheetStore.selectedTeeTime.id);

    if (nextTeeTimes == null) {
      return;
    }

    //Delete all tee time locks created by the user
    const teeTimeLockDeleteRes = await DeleteTeeTimeLocks(true);
    if (teeTimeLockDeleteRes.status !== StatusCode.OK) {
      return;
    }

    let quantityRemaining = quantity - teeSheetStore.selectedTeeTime?.slots?.length;

    nextTeeTimes.forEach((teeTime, i) => {
      nextTeeTimeSlots.push([]);
      if (teeTime.lock !== null && teeTime.lock.user_id !== authStore.user.id) {
        //Check if the tee times have a lock and if the lock was created by another user
        errorCheck = true;
        updateState<INewBookingState>(
          { teetimeLockPopupVisible: true, lockedTeeTime: convertTime(teeTime.start_time) },
          StateType.NewBookingState,
        );

        return;
      } else if (
        teeTime.slots.some(slot => {
          //Add the required amount of tee time slots
          if (quantityRemaining > 0) {
            nextTeeTimeSlots[i].push(slot);
          }
          quantityRemaining -= 1;
          //Check if the tee times contain booked slots
          return slot.status !== "available";
        })
      ) {
        errorCheck = true;
        dispatch(showError(t("Tee time at ") + convertTime(teeTime.start_time) + t(" already contains a booking")));
        return;
      }

      if (teeTime.turn_tee_time?.id != null) {
        nextTurnTeeTimeIds.push(teeTime.turn_tee_time.id);
      }

      tee_time_ids.push(teeTime.id);
    });

    //Post tee time locks for the current tee time and the next required tee times
    if (!errorCheck) {
      const teeTimeLockRes = await PostTeeTimeLock({ tee_time_ids }, true);
      if (teeTimeLockRes.status !== StatusCode.OK) {
        errorCheck = true;
      }
    } else if (errorCheck) {
      //If there is an error, we still need to POST tee time lock for the current selected tee time
      await PostTeeTimeLock({ tee_time_id: teeSheetStore.selectedTeeTime.id }, true);
      return;
    }

    updateState<INewBookingState>(
      {
        quantity,
        extraPlayersQuantity: quantity - teeSheetStore.selectedTeeTime?.slots?.length,
        extraPlayersDropdownVisible: false,
        extraPlayersSlots: nextTeeTimeSlots,
        extraPlayersTurnTeeTimeIds: nextTurnTeeTimeIds,
      },
      StateType.NewBookingState,
    );
  };

  const getNextTeeTimes = async (limit: number) => {
    const start_time = props.getNextTeeTimeStartTime(teeSheetStore.selectedTeeTime);

    //Check if the next tee time is undefined (if current tee time is the last tee time)
    if (!start_time) {
      dispatch(showError(t("No more tee times available")));
      return null;
    }

    let teeTimesRes: IGetTeeTimeRes;
    let teeTimes: ITeeTime[] = [];

    if (teeSheetStore.selectedTeeTime?.shotgun_application_id) {
      //check shotgun teetimes
      teeTimesRes = await GetTeeTime(
        {
          date: teeSheetStore.selectedTeeTime.date,
          blocked_type: "shotgun",
          division_id: teeSheetStore.selectedTeeTime.division_id,
          start_time: teeSheetStore.selectedTeeTime.start_time,
          // limit: limit + 1,
          extended: true,
          locks: true,
          turn_tee_time: true,
          course_id: teeSheetStore.selectedTeeTime.course_id,
        },
        true,
      );

      const filteredTeeTimes = teeTimesRes?.data?.filter(
        teetime =>
          teetime.start_time === teeSheetStore.selectedTeeTime.start_time && teetime.slots[0].status === "available",
      );

      const selectedTeeTimeIndex = filteredTeeTimes?.findIndex(
        teeTime => teeTime.id === teeSheetStore.selectedTeeTime.id,
      );

      if (selectedTeeTimeIndex == null || selectedTeeTimeIndex === -1) {
        dispatch(showError(t("Error loading selected shotgun tee time")));
        return null;
      }

      teeTimes = filteredTeeTimes.splice(selectedTeeTimeIndex, limit + 1);
    } else {
      teeTimesRes = await GetTeeTime(
        {
          date: teeSheetStore.selectedTeeTime.date,
          blocked_type: "open",
          division_id: teeSheetStore.selectedTeeTime.division_id,
          start_time: teeSheetStore.selectedTeeTime.start_time,
          limit: limit + 1,
          extended: true,
          locks: true,
          turn_tee_time: true,
          course_id: teeSheetStore.selectedTeeTime.course_id,
        },
        true,
      );

      teeTimes = teeTimesRes.data;
    }

    if (teeTimesRes.status !== StatusCode.OK) {
      dispatch(showError(teeTimesRes.message));
      return null;
    } else if (teeTimes?.length - 1 !== limit) {
      //Check if there are enough tee times left to fill the selected amount of players
      dispatch(showError(t("Not enough tee times available for selected number of players")));
      return null;
    }

    return teeTimes;
  };

  const handleExtraPlayersVisible = () => {
    updateState<INewBookingState>({ extraPlayersDropdownVisible: true }, StateType.NewBookingState);
  };

  const handleExtraCartsVisible = () => {
    updateState<INewBookingState>({ extraCartsDropdownVisible: true }, StateType.NewBookingState);
  };

  const handleClosePopup = () => {
    updateState<INewBookingState>({ teetimeLockPopupVisible: false }, StateType.NewBookingState);
  };

  const toggleNewCustomerSheet = (slot: ISlot) => {
    updateState<INewPlayerState>(
      {
        selectedSlot: slot,
        newCustomerSheetActive: !newPlayerState.newCustomerSheetActive,
      },
      StateType.NewPlayerState,
    );

    openNewCustomerSheet();
  };

  useEffect(() => {
    if (newBookingState.customerSelection[0]?.id) {
      void loadCustomersPaymentMethods(newBookingState.customerSelection[0].id);
    } else {
      updateState<INewBookingState>(
        { selectedPaymentMethod: undefined, paymentMethods: undefined },
        StateType.NewBookingState,
      );
    }
  }, [newBookingState.customerSelection[0]]);

  function openNewCustomerSheet() {
    setNewPlayerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: true,
    }));
  }

  useEffect(() => {
    console.log("Customer selection changed: ", newBookingState.customerSelection);
  }, [newBookingState.customerSelection]);

  return (
    <div>
      <NewCustomer
        newCustomerSheetActive={newPlayerState.newCustomerSheetActive}
        onCancel={closeNewCustomer}
        onOk={info => void addCustomer(info)}
        searchValue={newPlayerState.currentSearch}
      />

      <Portal isMounted={uiStore.newBookingActive}>
        <Sheet
          open={uiStore.newBookingActive}
          size="medium"
          closable={!newBookingState.bookingPosting}
          title={t("New Booking")}
          height="flexible"
          overflow
          onCancel={closeNewBooking}
          onOk={
            !cc_override && cc_required && cc_unselected && !newBookingState?.bookingWithinHour
              ? () => handleTabChange(1)
              : bookCustomer
          }
          cancelText={t("Clear")}
          okText={
            !cc_override && cc_required && cc_unselected && !newBookingState.bookingWithinHour
              ? t("Enter Credit Card")
              : t("Save Booking")
          }
          okDisabled={
            newBookingState.customerSelection.length === 0 ||
              newBookingState.holes === null ||
              newBookingState.quantity === null ||
              newBookingState.powerCartQuantity === null ||
              newBookingState.bookingPosting
              ? true
              : false
          }
          cancelDisabled={newBookingState.bookingPosting}
          backDropCancel={false}
        >
          {props.loading || newBookingState.bookingPosting ? (
            <div className="text-center">
              <div style={{ height: "32px", overflow: "hidden" }}>
                <Spin />
              </div>
              <div className="mt-2">
                {newBookingCount > 0
                  ? `${newBookingCount} / ${Math.ceil(
                    newBookingState.quantity / teeSheetStore.selectedTeeTime?.slots?.length,
                  )} Bookings Completed`
                  : props.loading
                    ? props.loadingText
                    : t("Creating Booking")}
              </div>
            </div>
          ) : (
            <>
              <div className="booking-datetime">
                <div className="booking-datetime-time">
                  {moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format(
                    "LT",
                  )}
                </div>

                <div className="booking-datetime-date">
                  {moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format(
                    "LL",
                  )}
                </div>
              </div>

              <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} variant={"underline"}>
                {/* Booking Details */}

                {selected === 0 ? (
                  <>
                    <div className="booking-modal">
                      <div className="booking-details">
                        <div className="booking-options">
                          <div className="booking-options_option">
                            <h2 className="carts-section-title">{t("Holes")}</h2>

                            <Radio.Group name="holes_new" onChange={handleHolesChange} value={newBookingState.holes}>
                              {newBookingState.holeButtons?.map((disabled, i) => {
                                const holes = i === 0 ? 18 : 9;
                                return (
                                  <Radio.Button key={`holes_new_${holes}`} value={holes} disabled={disabled}>
                                    {convertHoles(
                                      holes,
                                      facilityStore.facility?.courses?.find(
                                        course => course.id === teeSheetStore.selectedTeeTime?.course_id,
                                      ),
                                    )}
                                  </Radio.Button>
                                );
                              })}
                            </Radio.Group>
                          </div>
                          <div className="booking-options_option">
                            <h2 className="carts-section-title">{t("Players")}</h2>
                            <div className="flex flex-row">
                              <Radio.Group
                                name="players_new"
                                onChange={(e: number) => void handlePlayersChange(e)}
                                value={newBookingState.quantity}
                              >
                                {newBookingState.playerButtons?.map((disabled, i) => {
                                  const players = i + 1;
                                  return (
                                    <Radio.Button key={`players_new_${players}`} value={players} disabled={disabled}>
                                      {players}
                                    </Radio.Button>
                                  );
                                })}

                                {/* Don't display 'More' button if there are booked players for this tee time */}
                                {teeSheetStore.selectedTeeTime?.quantity_remaining >=
                                  teeSheetStore.selectedTeeTime?.slots?.length ? (
                                  <Radio.Button
                                    onClick={handleExtraPlayersVisible}
                                    value={
                                      newBookingState.quantity > teeSheetStore.selectedTeeTime?.slots?.length
                                        ? newBookingState.quantity
                                        : -1
                                    }
                                  >
                                    {newBookingState.quantity > teeSheetStore.selectedTeeTime?.slots?.length
                                      ? newBookingState.quantity
                                      : "+"}
                                  </Radio.Button>
                                ) : null}
                              </Radio.Group>
                              <Dropdown
                                overlay={extraPlayersMenu()}
                                trigger="boolean"
                                placement="topLeft"
                                className="self-center extra-players-menu"
                                showMenu={newBookingState.extraPlayersDropdownVisible}
                                propRef={extraPlayersRef}
                                outSideClickDisable={true}
                              >
                                <span></span>
                              </Dropdown>
                            </div>
                          </div>
                          <div className="booking-options_option">
                            <h2 className="carts-section-title">{t("Power Carts")}</h2>

                            <Radio.Group
                              name="power_carts_new"
                              onChange={handleCartChange}
                              value={newBookingState.powerCartQuantity}
                            >
                              <Radio.Button
                                value={"none"}
                                disabled={teeSheetStore.selectedTeeTime?.cart_rule == "required"}
                              >
                                0
                              </Radio.Button>

                              <Radio.Button
                                value={1}
                                disabled={
                                  teeSheetStore.selectedTeeTime?.cart_rule == "none" ||
                                  (bookingCartsAvailable < 1 &&
                                    facilityStore.teeSheetSettings?.validate_power_cart_count)
                                }
                              >
                                1
                              </Radio.Button>

                              <Radio.Button
                                value={2}
                                disabled={
                                  teeSheetStore.selectedTeeTime?.cart_rule == "none" ||
                                  (bookingCartsAvailable < 2 &&
                                    facilityStore.teeSheetSettings?.validate_power_cart_count)
                                }
                              >
                                2
                              </Radio.Button>

                              {newBookingState.quantity > teeSheetStore.selectedTeeTime?.slots?.length ? (
                                <Radio.Button
                                  onClick={handleExtraCartsVisible}
                                  value={
                                    newBookingState.powerCartQuantity !== "none" &&
                                      Number(newBookingState.powerCartQuantity) > 2
                                      ? newBookingState.powerCartQuantity
                                      : -1
                                  }
                                >
                                  {newBookingState.powerCartQuantity !== "none" &&
                                    Number(newBookingState.powerCartQuantity) > 2
                                    ? newBookingState.powerCartQuantity
                                    : "+"}
                                </Radio.Button>
                              ) : null}

                              <Dropdown
                                overlay={extraPowerCartMenu()}
                                trigger="boolean"
                                placement="topRight"
                                className="self-center extra-carts-menu"
                                showMenu={newBookingState.extraCartsDropdownVisible}
                                propRef={extraCartsRef}
                                overlap
                                outSideClickDisable={true}
                              >
                                <span></span>
                              </Dropdown>
                            </Radio.Group>
                          </div>
                          {facilityStore.teeSheetSettings?.display_booking_source ? (
                            <div className="booking-options_option booking-source-option">
                              <h2 className="carts-section-title">{t("Source")}</h2>
                              <div className="flex flex-row">
                                <Radio.Group name="source_new" value={newBookingState.source}>
                                  <Radio.Button
                                    onClick={handleSourceDropdownVisible}
                                    value={newBookingState.source}
                                  >
                                    <FontAwesomeIcon
                                      icon={
                                        bookingSourceOptions.find(option => option.value === newBookingState.source)
                                          ?.icon ?? faPhone
                                      }
                                      title={
                                        bookingSourceOptions.find(option => option.value === newBookingState.source)
                                          ?.label ?? t("Phone")
                                      }
                                    />
                                  </Radio.Button>
                                </Radio.Group>
                                <Dropdown
                                  overlay={sourceMenu()}
                                  trigger="boolean"
                                  placement="topLeft"
                                  className="self-center booking-source-menu"
                                  showMenu={newBookingState.sourceDropdownVisible}
                                  propRef={sourceRef}
                                  outSideClickDisable={true}
                                >
                                  <span></span>
                                </Dropdown>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <div className="booking-customers-list" style={{ marginTop: "12px" }}>
                          {newBookingState?.quantity &&
                            teeSheetStore.selectedTeeTime?.slots
                              .filter(slot => slot.status === "available")
                              .map((slot: ISlot, index: number) => {
                                const golferPosition = slot.position.toString();
                                const queryKey = "golferSearchQuery".concat(golferPosition);
                                const searchResultKey = "golferSearchResult".concat(golferPosition);
                                //Get the matching slot that was added to the selection
                                const slotFound = newBookingState.teeTimeSlots.find(element => element.id === slot.id);
                                if (slotFound) {
                                  //Get the golfer info from customer selection
                                  const golfer = newBookingState.customerSelection.find(
                                    golfer => golfer.id === slotFound.customer_id,
                                  );
                                  if (golfer != null || slotFound.guest) {
                                    const guestName = [slotFound.first_name, slotFound.last_name]
                                      .filter(Boolean)
                                      .join(" ");
                                    return (
                                      <PlayerSlots
                                        closable
                                        removeGolfer={() => removeGolfer(slotFound)}
                                        email={slotFound.guest ? undefined : golfer?.email}
                                        name={slotFound.guest && guestName ? guestName : golfer?.full_name}
                                        memberCode={slotFound.guest ? undefined : golfer?.member_code}
                                        customerType={slotFound.guest ? undefined : golfer?.customer_type}
                                        phone={slotFound.guest ? undefined : golfer?.phone}
                                        isGuest={slotFound.guest}
                                        key={index}
                                      />
                                    );
                                  }
                                } else {
                                  return (
                                    <div key={index} className="booking-player-dropdown">
                                      <Select
                                        label={t("Player").concat(" " + golferPosition)}
                                        placeholder={t("Golfer")}
                                        showSearch
                                        className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                                        onSearch={(query: string) => handleCustomerSearch(query, queryKey)}
                                        onChange={(id: string | number, slot: ISlot) =>
                                          handleCustomerSelection(id, slot, null)
                                        }
                                        allowClear
                                        searchValue={state[queryKey]}
                                        showDropDownOnFocus={true}
                                        searching={state.searching}
                                        disabled={index + 1 <= newBookingState.quantity ? false : true}
                                        usePortal
                                        dropDownPositionBottom
                                        autoFocus={index + 1 === newBookingState.customerSelection?.length + 1}
                                      >
                                        <Select.Action
                                          className="ui-select-dropdown-list-item"
                                          onClick={() => toggleNewCustomerSheet(slot)}
                                        >
                                          <p className="text-md">{t("New Customer")}</p>
                                        </Select.Action>
                                        {state[queryKey] && slot.position !== 1 ? (
                                          <Select.Action
                                            className="ui-select-dropdown-list-item"
                                            onClick={() => handleSetAsGuest(slot)}
                                          >
                                            <p className="text-md">{t("Set as Guest")}</p>
                                          </Select.Action>
                                        ) : null}
                                        {((): (Partial<ICustomer> | ICustomer)[] => {
                                          const selectedIds = newBookingState.customerSelection.map(
                                            customer => customer?.id,
                                          );
                                          const partnerOptions = state.playingPartners.filter(
                                            partner => !selectedIds.includes(partner.id),
                                          );

                                          return state[queryKey]
                                            ? (state[searchResultKey] as (Partial<ICustomer> | ICustomer)[])
                                            : partnerOptions;
                                        })().map((golfer: any, i: number) => {
                                          const playingPartnerIds = new Set(
                                            state.playingPartners.map(partner => Number(partner.id)),
                                          );
                                          const isPlayingPartner = playingPartnerIds.has(Number(golfer?.id));
                                          const restrictions =
                                            teeSheetStore.selectedTeeTime.active_restrictions?.filter(
                                              restriction => restriction.customer_type_id === golfer?.customer_type_id,
                                            );
                                          //Banned golfer
                                          if (
                                            golfer != null &&
                                            (golfer?.banned ||
                                              restrictions.filter(restriction => restriction.block_bookings).length > 0)
                                          ) {
                                            return (
                                              <div
                                                key={i}
                                                className="flex justify-between bg-gray-100 cursor-not-allowed"
                                                style={{ padding: "8px 16px" }}
                                              >
                                                <div>
                                                  <div className="font-semibold text-lg flex items-center gap-2">
                                                    <span>{golfer?.full_name}</span>
                                                    {isPlayingPartner ? (
                                                      <Badge size="small" type="gray">
                                                        {t("Playing Partner")}
                                                      </Badge>
                                                    ) : null}
                                                  </div>
                                                  <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                  <div className="text-sm text-gray-500">{golfer.email}</div>
                                                  <div className="text-sm text-gray-500">
                                                    {golfer.phone ? golfer.phone : null}
                                                  </div>
                                                </div>
                                                <div className="flex flex-column justify-between">
                                                  <div className="flex flex-column gap-2">
                                                    {golfer.has_high_priority_notes ? (
                                                      <div
                                                        className="font-medium text-error self-end gap-2 flex"
                                                        style={{ alignItems: "center" }}
                                                        onClick={e => {
                                                          e.stopPropagation();
                                                          window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                        }}
                                                      >
                                                        <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                        <p>{t("Click to view notes")}</p>
                                                      </div>
                                                    ) : (
                                                      <div />
                                                    )}
                                                    {golfer?.banned ? (
                                                      <div className="ml-auto">
                                                        <Badge size="small" type="warning">
                                                          {t("Banned")}
                                                        </Badge>
                                                      </div>
                                                    ) : null}
                                                    {restrictions.length > 0 && !golfer?.banned ? (
                                                      <OverlayTrigger
                                                        placement="top-end"
                                                        overlay={
                                                          <Tooltip id="restricted-booking-tooltip">{`${t(
                                                            "Tee time outside of customer booking hours",
                                                          )}`}</Tooltip>
                                                        }
                                                      >
                                                        <div className="ml-auto">
                                                          <Badge size="small" type="warning">
                                                            <FontAwesomeIcon
                                                              className="mr-1"
                                                              icon={faCircleExclamation}
                                                            />
                                                            {t("Restricted")}
                                                          </Badge>
                                                        </div>
                                                      </OverlayTrigger>
                                                    ) : null}
                                                  </div>
                                                  <div className="font-medium text-base text-gray-500 self-end">
                                                    {golfer.member_code}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }
                                          if (golfer !== null && golfer !== undefined) {
                                            return (
                                              <Option key={i} value={golfer.id} extraValues={slot}>
                                                <div className="flex justify-between">
                                                  <div>
                                                    <div className="font-semibold text-lg flex items-center gap-2">
                                                      <span>{golfer?.full_name}</span>
                                                      {isPlayingPartner ? (
                                                        <Badge size="small" type="gray">
                                                          {t("Playing Partner")}
                                                        </Badge>
                                                      ) : null}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                    <div className="text-sm text-gray-500">{golfer.email}</div>
                                                    <div className="text-sm text-gray-500">
                                                      {golfer.phone ? golfer.phone : null}
                                                    </div>
                                                  </div>
                                                  <div className="flex flex-column justify-between">
                                                    {golfer.has_high_priority_notes ? (
                                                      <div
                                                        className="font-medium text-error self-end gap-2 flex"
                                                        style={{ alignItems: "center" }}
                                                        onClick={e => {
                                                          e.stopPropagation();
                                                          window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                        }}
                                                      >
                                                        <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                        <p>{t("Click to view notes")}</p>
                                                      </div>
                                                    ) : (
                                                      <div />
                                                    )}
                                                    {restrictions.length > 0 ? (
                                                      <OverlayTrigger
                                                        placement="top-end"
                                                        overlay={
                                                          <Tooltip id="restricted-booking-tooltip">{`${t(
                                                            "Tee time outside of customer booking hours",
                                                          )}`}</Tooltip>
                                                        }
                                                      >
                                                        <div className="ml-auto">
                                                          <Badge size="small" type="warning">
                                                            <FontAwesomeIcon
                                                              className="mr-1"
                                                              icon={faCircleExclamation}
                                                            />
                                                            {t("Restricted")}
                                                          </Badge>
                                                        </div>
                                                      </OverlayTrigger>
                                                    ) : null}
                                                    <div className="font-medium text-base text-gray-500 self-end">
                                                      {golfer.member_code}
                                                    </div>
                                                  </div>
                                                </div>
                                              </Option>
                                            );
                                          }
                                        })}
                                      </Select>
                                    </div>
                                  );
                                }
                              })}
                        </div>
                        {teeSheetStore.selectedTeeTime?.slots && (
                          <div className="flex items-end justify-end gap-4 mt-3">
                            <Checkbox
                              size="small"
                              checked={sendBookingConfirmation}
                              onChange={e => setSendBookingConfirmation(e.target.checked)}
                              label={t("Send Booking Confirmation")}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}

                {selected === 1 ? (
                  <div className="booking-credit-card-tab">
                    <CreditCardCatalogue
                      customerId={newBookingState.teeTimeSlots[0]?.customer_id}
                      creditCards={newBookingState.paymentMethods}
                      selectedCard={newBookingState.selectedPaymentMethod}
                      setSelectedCard={(paymentId: number) => onSelectCreditCard(paymentId)}
                    />
                    <CreditCardAdd
                      isSavingCard={newBookingState.saveCard}
                      setIsSavingCard={() =>
                        updateState<INewBookingState>(
                          { saveCard: !newBookingState.saveCard },
                          StateType.NewBookingState,
                        )
                      }
                      handleStripeInputs={handleCardSectionChange}
                    />
                  </div>
                ) : null}

                {selected === 2 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "12px",
                        }}
                      >
                        <span style={{ fontSize: "16px", fontWeight: "600", color: "#111827" }}>{t("Notes")}</span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "20px",
                            height: "20px",
                            padding: "0 6px",
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                            borderRadius: "10px",
                            fontSize: "12px",
                            fontWeight: "500",
                            lineHeight: "1",
                          }}
                        >
                          {newBookingState.notes?.length || 0}
                        </span>
                      </div>
                      <ButtonNew
                        type="text"
                        size="small"
                        onClick={() => {
                          updateState<INewBookingState>(
                            {
                              addNoteVisible: true,
                              noteDescription: "",
                            },
                            StateType.NewBookingState,
                          );
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: "4px" }} />
                        {t("Add note")}
                      </ButtonNew>
                    </div>

                    {/* Add note modal/card - only show when addNoteVisible is true */}
                    {newBookingState.addNoteVisible && (
                      <div
                        style={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E5E7EB",
                          borderRadius: "8px",
                          padding: "16px",
                        }}
                      >
                        <TextField
                          label={t("New Note")}
                          value={newBookingState.noteDescription}
                          onChange={handleNoteInput}
                          placeholder={t("Enter your note here...")}
                          rows={3}
                        />
                        <div className="pt-2 w-50">
                          <SelectNew
                            id="inlineNoteParticipant"
                            label={t("Participant")}
                            value={newBookingState.notePlayer}
                            onChange={e =>
                              setNewBookingState(prev => ({
                                ...prev,
                                notePlayer: e,
                              }))
                            }
                            placeholder={"Select a participant..."}
                            options={[0, 1, 2, 3, 4, 5]
                              .filter(
                                index =>
                                  index < 4 ||
                                  (teeSheetStore.selectedTeeTime?.slots?.length &&
                                    index < teeSheetStore.selectedTeeTime.slots.length),
                              )
                              .map(index => {
                                let label = `Player ${index + 1}`;

                                const availableSlots = teeSheetStore.selectedTeeTime?.slots?.filter(
                                  slot => slot.status === "available",
                                );
                                if (availableSlots && availableSlots[index]) {
                                  const slot = availableSlots[index];
                                  const slotFound = newBookingState.teeTimeSlots?.find(
                                    element => element.id === slot.id,
                                  );
                                  if (slotFound) {
                                    const golfer = newBookingState.customerSelection?.find(
                                      g => g.id === slotFound.customer_id,
                                    );
                                    if (golfer?.full_name) {
                                      label = golfer.full_name;
                                    }
                                  }
                                } else if (
                                  newBookingState.customerSelection &&
                                  newBookingState.customerSelection[index]?.full_name
                                ) {
                                  label = newBookingState.customerSelection[index].full_name;
                                }

                                return {
                                  label,
                                  id: index,
                                  disabled: newBookingState.quantity < index + 1,
                                };
                              })}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                            marginTop: "12px",
                          }}
                        >
                          <ButtonNew
                            type="secondary"
                            size="medium"
                            onClick={() => {
                              updateState<INewBookingState>(
                                {
                                  addNoteVisible: false,
                                  noteDescription: "",
                                },
                                StateType.NewBookingState,
                              );
                            }}
                          >
                            {t("Cancel")}
                          </ButtonNew>
                          <ButtonNew
                            type="primary"
                            size="medium"
                            disabled={!newBookingState.noteDescription.trim()}
                            onClick={() => {
                              if (!newBookingState.noteDescription.trim()) {
                                return;
                              }

                              const tempDate = new Date();
                              const date = moment(tempDate).format("LLL");
                              const dateString = "Added On: " + date;

                              const tempNote = {
                                dateString: dateString,
                                description: newBookingState.noteDescription,
                                user: authStore.user.full_name,
                                participantIndex: newBookingState.notePlayer,
                              };

                              const tempNotesArr = [...newBookingState.notes];
                              tempNotesArr.unshift(tempNote);

                              updateState<INewBookingState>(
                                {
                                  notes: tempNotesArr,
                                  noteDescription: "",
                                  addNoteVisible: false,
                                },
                                StateType.NewBookingState,
                              );
                            }}
                          >
                            {t("Save note")}
                          </ButtonNew>
                        </div>
                      </div>
                    )}

                    {/* Notes list or empty state */}
                    <div style={{ flex: 1, overflowY: "auto", maxHeight: "500px" }}>
                      {newBookingState.notes?.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          {newBookingState.notes.map((props: any, index: number) => (
                            <div
                              key={index}
                              style={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E5E7EB",
                                borderRadius: "8px",
                                padding: "16px",
                              }}
                            >
                              {/* Note content */}
                              <p
                                style={{
                                  color: "#111827",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  marginBottom: "12px",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {props.description}
                              </p>

                              {/* Footer with date and user */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    color: "#6B7280",
                                    fontSize: "13px",
                                  }}
                                >
                                  <span>{props.dateString}</span>
                                  <span>•</span>
                                  <span>{props.user}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "200px",
                            padding: "24px",
                          }}
                        >
                          <p style={{ color: "#9CA3AF", fontSize: "14px", textAlign: "center" }}>{t("No notes yet")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </Tabs>
            </>
          )}
        </Sheet>
      </Portal>

      <Popup
        open={newBookingState.teetimeLockPopupVisible}
        type="warning"
        title={t("Tee Time Locked!")}
        description={t("Tee time at ") + newBookingState.lockedTeeTime + t("is currently being booked by another user")}
        onOk={() => handleClosePopup()}
        okText={t("Go Back")}
        onCancel={() => handleClosePopup()}
        backDropCancel={true}
      />
    </div>
  );
}
